import { Box, Button, Icon, IconButton, Spinner, Tooltip, useToast } from '@chakra-ui/react';
import axios from 'axios';
import { TbEye } from 'react-icons/tb';
import { useGetPurchaseOrderPdf } from '../../hooks/inventory/useGetPurchaseOrderPdf';

function extractApiErrorMessage(error: unknown, fallback: string): string {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as { message?: string } | undefined;
        return data?.message?.trim() || fallback;
    }
    if (error instanceof Error && error.message.trim()) {
        return error.message.trim();
    }
    return fallback;
}

interface PurchaseOrderPdfButtonProps {
    purchaseOrderId: string;
    invoiceNumber: string;
    /** Icon-only with tooltip (Ordered tab). Default is the labeled View PO button. */
    variant?: 'label' | 'icon';
}

const PurchaseOrderPdfButton = ({
    purchaseOrderId,
    invoiceNumber,
    variant = 'label',
}: PurchaseOrderPdfButtonProps) => {
    const toast = useToast();
    const pdfMutation = useGetPurchaseOrderPdf();

    const handleView = async () => {
        try {
            const { url } = await pdfMutation.mutateAsync({ purchaseOrderId });
            const opened = window.open(url, '_blank', 'noopener,noreferrer');
            if (!opened) {
                toast({
                    title: 'Allow pop-ups to view the PDF',
                    status: 'warning',
                    duration: 4000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: 'Could not open PDF',
                description: extractApiErrorMessage(error, 'Please try again.'),
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        }
    };

    const viewLabel = `View PO invoice ${invoiceNumber}`;

    if (variant === 'icon') {
        return (
            <Tooltip label={viewLabel} placement="top" hasArrow>
                <Box as="span" display="inline-flex">
                    <IconButton
                        aria-label={viewLabel}
                        icon={
                            pdfMutation.isPending ? (
                                <Spinner size="xs" />
                            ) : (
                                <Icon as={TbEye as React.ElementType} boxSize={4} />
                            )
                        }
                        size="xs"
                        variant="ghost"
                        color="gray.600"
                        isDisabled={pdfMutation.isPending}
                        onClick={handleView}
                    />
                </Box>
            </Tooltip>
        );
    }

    return (
        <Button
            size="xs"
            variant="ghost"
            color="gray.700"
            fontWeight="medium"
            leftIcon={<Icon as={TbEye as React.ElementType} boxSize={3.5} />}
            isLoading={pdfMutation.isPending}
            onClick={handleView}
            aria-label={viewLabel}
            flexShrink={0}
            px={0}
        >
            View PO
        </Button>
    );
};

export default PurchaseOrderPdfButton;
